import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui';
import type { UserRole } from '@/types';

interface QuickAction {
  title: string;
  description: string;
  href: string;
  variant?: 'primary' | 'secondary' | 'outline';
}

interface QuickActionsProps {
  role: UserRole;
}

const roleActions: Record<UserRole, QuickAction[]> = {
  sales_ae: [
    {
      title: 'Create Hold Request',
      description: 'Create new hold request with line items',
      href: '/holds/create',
      variant: 'primary',
    },
    {
      title: 'My Requests',
      description: 'View and manage your hold requests',
      href: '/holds',
      variant: 'secondary',
    },
  ],
  service_ae: [
    {
      title: 'Cross-Region Requests',
      description: 'View requests where you are assigned',
      href: '/holds?filter=cross-region',
      variant: 'primary',
    },
    {
      title: 'My Assignments',
      description: 'Line items assigned to you',
      href: '/holds?filter=assigned',
      variant: 'secondary',
    },
  ],
  sales_support: [
    {
      title: 'Create Hold Request',
      description: 'Create new hold request with line items',
      href: '/holds/create',
      variant: 'primary',
    },
    {
      title: 'All Requests',
      description: 'View all hold requests',
      href: '/holds',
      variant: 'secondary',
    },
  ],
  sales_vp: [
    {
      title: 'Team Requests',
      description: 'View requests from your team',
      href: '/holds?filter=team',
      variant: 'primary',
    },
    {
      title: 'Create Hold Request',
      description: 'Create new hold request with line items',
      href: '/holds/create',
      variant: 'secondary',
    },
    {
      title: 'Reports',
      description: 'View performance reports',
      href: '/reports',
      variant: 'outline',
    },
  ],
  gvp: [
    {
      title: 'Enterprise Overview',
      description: 'View all requests across regions',
      href: '/holds',
      variant: 'primary',
    },
    {
      title: 'Create Hold Request',
      description: 'Create new hold request with line items',
      href: '/holds/create',
      variant: 'secondary',
    },
    {
      title: 'Analytics',
      description: 'View enterprise analytics',
      href: '/analytics',
      variant: 'outline',
    },
  ],
  rvp: [
    {
      title: 'Regional Overview',
      description: 'View requests in your region',
      href: '/holds?filter=region',
      variant: 'primary',
    },
    {
      title: 'Team Performance',
      description: 'View team metrics',
      href: '/reports/team',
      variant: 'secondary',
    },
  ],
  charting_member: [
    {
      title: 'Work Queue',
      description: 'View and process line items',
      href: '/charting',
      variant: 'primary',
    },
    {
      title: 'My Assignments',
      description: 'View line items assigned to you',
      href: '/charting?filter=mine',
      variant: 'secondary',
    },
  ],
  charting_manager: [
    {
      title: 'Work Queue',
      description: 'Manage team assignments',
      href: '/charting',
      variant: 'primary',
    },
    {
      title: 'Team Performance',
      description: 'View team metrics',
      href: '/charting?view=team',
      variant: 'secondary',
    },
  ],
  exceptions_member: [
    {
      title: 'Exception Queue',
      description: 'Review exception hold requests',
      href: '/exceptions',
      variant: 'primary',
    },
    {
      title: 'Pending Approvals',
      description: 'Items awaiting your approval',
      href: '/exceptions?status=in_review',
      variant: 'secondary',
    },
    {
      title: 'All Requests',
      description: 'Enterprise-wide visibility',
      href: '/exceptions?view=all',
      variant: 'outline',
    },
  ],
  adops_leadership: [
    {
      title: 'All Requests',
      description: 'View all hold requests',
      href: '/holds',
      variant: 'primary',
    },
    {
      title: 'Exception Approvals',
      description: 'Review exception requests',
      href: '/exceptions',
      variant: 'secondary',
    },
    {
      title: 'User Management',
      description: 'Manage users and permissions',
      href: '/admin/users',
      variant: 'outline',
    },
  ],
  admin: [
    {
      title: 'User Management',
      description: 'Manage user accounts and roles',
      href: '/admin/users',
      variant: 'primary',
    },
    {
      title: 'All Requests',
      description: 'View all hold requests (read-only)',
      href: '/holds',
      variant: 'secondary',
    },
    {
      title: 'Settings',
      description: 'Notification preferences',
      href: '/settings',
      variant: 'outline',
    },
  ],
};

export function QuickActions({ role }: QuickActionsProps) {
  const actions = roleActions[role] || [];

  if (actions.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick Actions</CardTitle>
        <CardDescription>Common tasks for your role</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {actions.map((action) => (
            <Link key={action.href} to={action.href} className="block">
              <div className="p-4 border border-neutral-200 rounded-lg hover:border-primary-500 transition-all">
                <h3 className="font-semibold text-neutral-900 mb-1">{action.title}</h3>
                <p className="text-sm text-neutral-600">{action.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
